<script lang="ts">
  import { onMount } from 'svelte';
  import {
    getPersonas, setActivePersona, updatePersona, type PersonasData,
    getBusinessConfig, updateBusinessConfig, type BusinessConfig,
  } from '../lib/api';
  import { agentName as agentNameStore, currentView } from '../lib/stores';
  import { workflow, isAdmin } from '../lib/stores';
  import { getFundWorkspace, saveFundProfile, uploadFundDocument, type FundWorkspace } from '../lib/api/fund-ir';
  import KnowledgeBase from './KnowledgeBase.svelte';
  import OutreachDocs from './OutreachDocs.svelte';
  import { downloadSupportFile, isSupportView } from '../lib/supportView';

  type Tab = 'groundwork' | 'knowledge' | 'collateral';
  const TABS: Tab[] = ['groundwork', 'knowledge', 'collateral'];

  // Tab state lives in the URL (?tab=…) so a refresh keeps the pane and the
  // tabs are linkable. App.svelte only ever pushes the *pathname* (/teach), so
  // writing the query string here never fights its currentView subscriber.
  function tabFromUrl(): Tab {
    if (typeof window === 'undefined') return 'groundwork';
    const t = new URLSearchParams(window.location.search).get('tab');
    return (TABS as string[]).includes(t || '') ? (t as Tab) : 'groundwork';
  }
  let activeTab = $state<Tab>(tabFromUrl());

  function selectTab(tab: Tab) {
    activeTab = tab;
    if (typeof window === 'undefined') return;
    const url = tab === 'groundwork' ? '/teach' : `/teach?tab=${tab}`;
    history.replaceState({ view: 'teach' }, '', url);
  }

  // Counts come from the same fetch that produces each list — the child pane
  // reports upward. All three panes stay mounted (hidden with CSS, like the
  // mockup) so the counts are populated before you first open the tab.
  let knowledgeCount = $state(0);
  let collateralCount = $state(0);
  let fundWorkspace = $state<FundWorkspace | null>(null);
  let fundLoading = $state(false);
  let fundError = $state('');
  let fundNameDraft = $state('');
  let fundVehiclesDraft = $state<{ name: string; strategy: string }[]>([]);
  let fundEditing = $state(false);
  let fundSaving = $state(false);
  let fundUploading = $state(false);

  async function loadFund() {
    fundLoading = true;
    try {
      fundWorkspace = await getFundWorkspace();
      fundNameDraft = fundWorkspace.profile.manager_name;
      if (!fundEditing) fundVehiclesDraft = fundWorkspace.profile.funds.map(fund => ({ ...fund }));
      fundError = '';
    } catch (err: any) {
      fundError = err?.message || 'Could not load your IR materials.';
    } finally {
      fundLoading = false;
    }
  }

  async function saveFundOrganization() {
    if (!fundWorkspace || !fundNameDraft.trim() || fundSaving
        || fundVehiclesDraft.some(fund => !fund.name.trim())) return;
    fundSaving = true;
    try {
      const saved = await saveFundProfile({ ...fundWorkspace.profile,
        manager_name: fundNameDraft.trim(),
        funds: fundVehiclesDraft.map(fund => ({ name: fund.name.trim(), strategy: fund.strategy.trim() })),
      });
      fundWorkspace = { ...fundWorkspace, profile: saved };
      fundEditing = false;
      await loadFund();
      showToast('Organization saved');
    } catch (err: any) {
      showToast('Could not save the organization: ' + (err?.message || 'request failed'), true);
    } finally {
      fundSaving = false;
    }
  }

  function startFundOrganizationEdit() {
    if (!fundWorkspace) return;
    fundNameDraft = fundWorkspace.profile.manager_name;
    fundVehiclesDraft = fundWorkspace.profile.funds.map(fund => ({ ...fund }));
    fundEditing = true;
  }

  function cancelFundOrganizationEdit() {
    fundEditing = false;
    fundNameDraft = fundWorkspace?.profile.manager_name || '';
    fundVehiclesDraft = fundWorkspace?.profile.funds.map(fund => ({ ...fund })) || [];
  }

  async function addFundDocument(event: Event) {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || fundUploading) return;
    fundUploading = true;
    try {
      await uploadFundDocument(file, activeTab === 'collateral' ? 'collateral' : 'fund');
      await loadFund();
      showToast('Document stored');
    } catch (err: any) {
      showToast('Could not upload the document: ' + (err?.message || 'request failed'), true);
    } finally {
      fundUploading = false;
      input.value = '';
    }
  }

  let toast = $state('');
  // Failures must render via `.toast-error`, never the success box:
  // DESIGN-SYSTEM §1 reserves green for on-track status.
  let toastError = $state(false);
  function showToast(msg: string, isError = false) {
    toast = msg;
    toastError = isError;
    setTimeout(() => toast = '', isError ? 5000 : 3000);
  }

  // ─── Company profile ───────────────────────────────────────────────────────

  let biz = $state<BusinessConfig | null>(null);
  let bizEditing = $state(false);
  let bizSaving = $state(false);
  let bizDraft = $state<BusinessConfig | null>(null);
  // Without this the failed-GET case rendered a "Company" heading with nothing
  // under it and no Edit button — an empty section that looks like a layout bug.
  let bizLoadFailed = $state(false);

  function startBizEdit() {
    bizDraft = { ...biz! };
    bizEditing = true;
  }
  function cancelBizEdit() {
    bizEditing = false;
    bizDraft = null;
  }
  // Every write on this page follows the same rule, because a browser-driven
  // audit caught all three stranding the page: `finally` always clears the busy
  // flag, a failure keeps you in edit mode with your draft intact rather than
  // discarding it, and the user is told. Before this, aborting the PUT left the
  // button reading "Saving..." and disabled forever — the only exit was a
  // reload, which threw the edit away.
  async function handleBizSave() {
    if (!bizDraft) return;
    bizSaving = true;
    // Send only the profile fields this card owns. The backend upserts per key,
    // so anything omitted is left alone — in particular Settings' personal-email
    // scope rule, which would otherwise be reverted to whatever this page loaded.
    const d = bizDraft;
    try {
      await updateBusinessConfig({
        company_name: d.company_name,
        industry: d.industry,
        target_audience: d.target_audience,
        outreach_objective: d.outreach_objective,
        self_serve_url: d.self_serve_url,
        product_name: d.product_name,
        product_description: d.product_description,
        industry_search_terms: d.industry_search_terms,
        news_search_terms: d.news_search_terms,
      });
      biz = await getBusinessConfig();
      bizEditing = false;
      bizDraft = null;
      showToast('Business profile updated');
    } catch (err: any) {
      showToast('Could not save the business profile: ' + (err?.message || 'request failed'), true);
    } finally {
      bizSaving = false;
    }
  }

  // ─── Personas ──────────────────────────────────────────────────────────────

  let data = $state<PersonasData | null>(null);
  let openKey = $state('');
  let saving = $state('');
  let editingKey = $state('');
  let editName = $state('');
  let editBg = $state('');
  let editStyle = $state('');

  // The live build encodes "this persona is a human voice, not the assistant"
  // by appending (Human)/(Emulate) to the label. Split it back out so the
  // names line up and the distinction reads as a status pill instead.
  const HUMAN_RE = /\s*\((Human|Emulate)\)/i;
  const isHuman = (label: string) => HUMAN_RE.test(label || '');
  const displayName = (label: string, key: string) => (label || key).replace(HUMAN_RE, '').trim() || key;

  // Human personas get an initials monogram where the agent gets its ✦, so the
  // mark slot is never an empty gap and both names still start at the same x.
  function initials(label: string, key: string): string {
    const parts = displayName(label, key).split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  // Default persona first — it's the one that actually writes, and the stored
  // dict order is arbitrary (it put the Human voice above George on geomet.ai).
  const personaList = $derived(
    data
      ? Object.entries(data.personas)
          .filter(([, p]) => !(p as any).special)
          .sort(([a], [b]) => (a === data!.active ? -1 : b === data!.active ? 1 : 0))
      : []
  );

  // Mirrors App.svelte's resolution: the agent is the active persona unless
  // that is a Human/Emulate voice, in which case it's the first real one.
  // Persona keys are opaque hashes ('a601bffc'), never the literal 'george'.
  // Comparing against that literal silently never matches.
  function syncAgentName() {
    if (!data) return;
    const all = Object.values(data.personas);
    let agent = data.active ? data.personas[data.active] : null;
    if (!agent || isHuman(agent.label)) agent = all.find(p => !isHuman(p.label)) || agent;
    if (agent?.label) $agentNameStore = agent.label.replace(HUMAN_RE, '').trim();
  }

  async function handleSetActive(key: string) {
    try {
      await setActivePersona(key);
      data = await getPersonas();
      syncAgentName();
      showToast(`Default persona set to ${displayName(data!.personas[key].label, key)}`);
    } catch (err: any) {
      // Nothing local changed, so there is nothing to revert — but silence here
      // reads as "it worked", and this decides which persona actually writes.
      showToast('Could not set the default persona: ' + (err?.message || 'request failed'), true);
    }
  }

  function toggleOpen(key: string) {
    openKey = openKey === key ? '' : key;
  }

  function startEdit(key: string) {
    editingKey = key;
    openKey = key;
    editName = data!.personas[key].label || key;
    editBg = data!.personas[key].background;
    editStyle = data!.personas[key].style;
  }
  function cancelEdit() {
    editingKey = '';
  }
  async function handleSave() {
    saving = editingKey;
    try {
      await updatePersona(editingKey, { name: editName, background: editBg, style: editStyle });
      data = await getPersonas();
      syncAgentName();
      editingKey = '';
      showToast('Persona updated');
    } catch (err: any) {
      // Stay in edit mode: `editName`/`editBg`/`editStyle` still hold what was
      // typed, and a background here runs to ~1,500 characters.
      showToast('Could not save the persona: ' + (err?.message || 'request failed'), true);
    } finally {
      saving = '';
    }
  }

  onMount(async () => {
    if ($workflow === 'fund') {
      await loadFund();
      return;
    }
    const [personas, bizData] = await Promise.all([
      getPersonas().catch(() => null),
      getBusinessConfig().catch(() => null),
    ]);
    data = personas;
    biz = bizData;
    bizLoadFailed = bizData === null;
    // Open the default persona on load — it's the one that actually writes.
    if (personas?.active) openKey = personas.active;
  });
</script>

<div class="page-title">Teach {$agentNameStore ? $agentNameStore.split(' ')[0] : 'Dealton'}</div>
<div class="page-subtitle">{$workflow === 'fund' ? "Your firm's identity and IR materials." : 'What he knows and who he writes as.'}</div>

<div class="tabs" role="tablist" aria-label="Knowledge tabs">
  <button type="button" role="tab" class="tab" class:active={activeTab === 'groundwork'} aria-selected={activeTab === 'groundwork'} onclick={() => selectTab('groundwork')}>Groundwork</button>
  <button type="button" role="tab" class="tab" class:active={activeTab === 'knowledge'} aria-selected={activeTab === 'knowledge'} onclick={() => selectTab('knowledge')}>Knowledge <span class="tab-count">{$workflow === 'fund' ? fundWorkspace?.documents.filter(d => d.category === 'fund').length ?? 0 : knowledgeCount}</span></button>
  <button type="button" role="tab" class="tab" class:active={activeTab === 'collateral'} aria-selected={activeTab === 'collateral'} onclick={() => selectTab('collateral')}>Collateral <span class="tab-count">{$workflow === 'fund' ? fundWorkspace?.documents.filter(d => d.category === 'collateral').length ?? 0 : collateralCount}</span></button>
</div>

{#if $workflow === 'fund'}
  {#if fundLoading && !fundWorkspace}
    <div class="empty-state" role="status">Loading Investor Relations materials…</div>
  {:else if fundError}
    <div class="empty-state" role="alert">{fundError} <button type="button" onclick={loadFund}>Retry</button></div>
  {:else if fundWorkspace}
    <div class="pane" class:on={activeTab === 'groundwork'}>
      <div class="subhead sales first"><span>Organization</span><span class="sp"></span>
        {#if $isAdmin && !fundEditing}<button class="today-mini-btn" onclick={startFundOrganizationEdit}>Edit</button>{/if}
      </div>
      <div class="card">
        {#if fundEditing}
          <div class="egrid"><div class="ef"><label for="fund-manager-name">Manager / firm name</label><input id="fund-manager-name" bind:value={fundNameDraft} maxlength="160" /></div></div>
          <p class="fund-doc-note">Your firm is one IR workspace. Fund vehicles below provide optional context for briefs and drafts.</p>
          {#each fundVehiclesDraft as fund, index}
            <div class="egrid">
              <div class="ef"><label for={`fund-vehicle-name-${index}`}>Fund vehicle {index + 1}</label><input id={`fund-vehicle-name-${index}`} bind:value={fund.name} maxlength="160" /></div>
              <div class="ef"><label for={`fund-vehicle-strategy-${index}`}>Strategy (optional)</label><textarea id={`fund-vehicle-strategy-${index}`} bind:value={fund.strategy} maxlength="1000" rows="2"></textarea></div>
              <button class="today-mini-btn" type="button" onclick={() => fundVehiclesDraft = fundVehiclesDraft.filter((_, i) => i !== index)}>Remove vehicle</button>
            </div>
          {/each}
          <button class="today-mini-btn" type="button" disabled={fundVehiclesDraft.length >= 20} onclick={() => fundVehiclesDraft = [...fundVehiclesDraft, { name: '', strategy: '' }]}>+ Add fund vehicle</button>
          <div class="editbar"><button class="btn-primary" disabled={fundSaving || !fundNameDraft.trim() || fundVehiclesDraft.some(fund => !fund.name.trim())} onclick={saveFundOrganization}>{fundSaving ? 'Saving…' : 'Save'}</button><button class="today-mini-btn" onclick={cancelFundOrganizationEdit}>Cancel</button></div>
        {:else}
          <!-- Everything onboarding actually stored, not just the firm name. Sector focus
               and target investor types were captured in the wizard and sat unrendered, so
               this page read as empty for a firm that left fund vehicles blank. -->
          <div class="pgrid">
            <div class="pf"><div class="k">Firm</div><div class="v">{fundWorkspace.profile.manager_name}</div></div>
            {#if fundWorkspace.profile.sector_focus.length}
              <div class="pf"><div class="k">Sector focus</div><div class="v">{fundWorkspace.profile.sector_focus.join(' · ')}</div></div>
            {/if}
            {#if fundWorkspace.profile.target_lp_types.length}
              <!-- The wizard accepts real custom categories here, so render them verbatim
                   rather than relabelling them as LP types. -->
              <div class="pf"><div class="k">Target investor types</div><div class="v">{fundWorkspace.profile.target_lp_types.join(' · ')}</div></div>
            {/if}
          </div>
        {/if}
      </div>
      {#if !fundEditing}<div class="subhead sales"><span>Fund vehicles</span></div>
      {#if fundWorkspace.profile.funds.length}
        <div class="card"><ul>{#each fundWorkspace.profile.funds as fund}<li><strong>{fund.name}</strong>{#if fund.strategy} · {fund.strategy}{/if}</li>{/each}</ul></div>
      {:else}<div class="empty-state">No fund vehicles added. Your firm remains one IR workspace.</div>{/if}{/if}
    </div>
    {#each ['knowledge', 'collateral'] as category}
      {@const docs = fundWorkspace.documents.filter(d => d.category === (category === 'knowledge' ? 'fund' : 'collateral'))}
      <div class="pane" class:on={activeTab === category}>
        <div class="subhead sales first"><span>{category === 'knowledge' ? 'Fund documents' : 'Collateral'}</span></div>
        <p class="fund-doc-note">Stored for your team. When preparing an LP brief or draft, you can explicitly choose text-readable files to use as sources; unselected files are not read for generation.</p>
        <label class="today-mini-btn fund-upload">Upload {category === 'knowledge' ? 'fund document' : 'collateral'}<input type="file" accept=".pdf,.docx,.doc,.txt,.md,.csv,.xlsx,.xls,.pptx,.jpg,.jpeg,.png,.webp" disabled={fundUploading || isSupportView()} onchange={addFundDocument} /></label>
        {#if docs.length}<div class="file-list">{#each docs as doc}<div class="file-list-item"><a href={`/api/fund/documents/${encodeURIComponent(doc.document_id)}`} onclick={(e) => void downloadSupportFile(e, `/api/fund/documents/${encodeURIComponent(doc.document_id)}`, doc.filename).catch((err) => alert(err.message))}>{doc.title}</a><span class="fund-doc-meta">{doc.filename}</span></div>{/each}</div>{:else}<div class="empty-state">No documents uploaded.</div>{/if}
      </div>
    {/each}
  {/if}
{:else}
<!-- ════════ GROUNDWORK ════════ -->
<div class="pane" class:on={activeTab === 'groundwork'}>

  <!-- ── COMPANY ── -->
  <div class="subhead sales first">
    <span>Company</span>
    {#if biz && !bizEditing}<span class="sp"></span><button class="today-mini-btn" onclick={startBizEdit}>Edit</button>{/if}
  </div>

  {#if bizLoadFailed}
    <!-- Was: nothing at all, under a "Company" heading that stayed. An empty
         section reads as a layout bug, not as a failed request. -->
    <div class="card">
      <div class="loadfail">Couldn't load the business profile. Reload the page to try again.</div>
    </div>
  {:else if biz}
    <div class="card">
      {#if bizEditing && bizDraft}
        <div class="egrid">
          <div class="ef">
            <label for="bz-company">Company</label>
            <input id="bz-company" type="text" bind:value={bizDraft.company_name} />
          </div>
          <div class="ef">
            <label for="bz-industry">Industry</label>
            <input id="bz-industry" type="text" bind:value={bizDraft.industry} placeholder="e.g. mining" />
          </div>
          <div class="ef">
            <label for="bz-audience">Target audience</label>
            <input id="bz-audience" type="text" bind:value={bizDraft.target_audience} placeholder="e.g. mining operators" />
          </div>
          <div class="ef">
            <label for="bz-objective">Outreach objective</label>
            <select id="bz-objective" bind:value={bizDraft.outreach_objective}>
              <option value="call">Book a call</option>
              <option value="self_serve">Self-serve trial</option>
            </select>
          </div>
          <div class="ef">
            <label for="bz-product">Product name</label>
            <input id="bz-product" type="text" bind:value={bizDraft.product_name} />
          </div>
          {#if bizDraft.outreach_objective === 'self_serve'}
            <div class="ef">
              <label for="bz-selfserve">Sign-up / trial link</label>
              <input id="bz-selfserve" type="url" bind:value={bizDraft.self_serve_url} placeholder="https://yourproduct.com/signup" />
            </div>
          {/if}
          <div class="ef wide">
            <label for="bz-proddesc">Product description</label>
            <textarea id="bz-proddesc" rows="2" bind:value={bizDraft.product_description}
              placeholder="e.g. deposit modelling system"></textarea>
          </div>
          <!-- ⚠️ These two hold onboarding-generated concatenations, e.g.
               'mining operators Mining' (target_audience + industry) and
               'Mining news mining operators'. They feed prospecting searches,
               so the low quality is worth a look — but it is a data problem in
               onboarding, not a rendering one. -->
          <div class="ef">
            <label for="bz-industryterms">Industry search terms</label>
            <input id="bz-industryterms" type="text" bind:value={bizDraft.industry_search_terms}
              placeholder="e.g. mining operations site overview" />
          </div>
          <div class="ef">
            <label for="bz-newsterms">News search terms</label>
            <input id="bz-newsterms" type="text" bind:value={bizDraft.news_search_terms} placeholder="e.g. mining news" />
          </div>
        </div>
        <div class="editbar">
          <button class="btn-primary" onclick={handleBizSave} disabled={bizSaving}>
            {bizSaving ? 'Saving...' : 'Save'}
          </button>
          <button class="today-mini-btn" onclick={cancelBizEdit}>Cancel</button>
          <span class="sp"></span>
          <span class="editmeta">Applies to new drafts only</span>
        </div>
      {:else}
        <div class="pgrid">
          <div class="pf"><div class="k">Company</div><div class="v">{biz.company_name || '—'}</div></div>
          <div class="pf"><div class="k">Industry</div><div class="v">{biz.industry || '—'}</div></div>
          <div class="pf"><div class="k">Target audience</div><div class="v">{biz.target_audience || '—'}</div></div>
          <div class="pf">
            <div class="k">Outreach objective</div>
            <div class="v">{(biz.outreach_objective || 'call') === 'self_serve' ? 'Self-serve trial' : 'Book a call'}</div>
            {#if (biz.outreach_objective || 'call') === 'self_serve' && biz.self_serve_url}
              <div class="v"><a href={biz.self_serve_url} target="_blank" rel="noreferrer">{biz.self_serve_url}</a></div>
            {/if}
          </div>
          <div class="pf wide">
            <div class="k">Product</div>
            <div class="v">{biz.product_name}{biz.product_description ? ` — ${biz.product_description}` : ''}</div>
          </div>
          <!-- industry_search_terms / news_search_terms are deliberately NOT in
               read mode. They were edit-only in the previous Business Profile
               too, and the mockup shows the same five fields. They hold
               auto-generated concatenations ("mining operators Mining") that
               read as noise next to curated copy — but they stay editable in
               the form below, so nothing became uneditable by hiding them. -->

        </div>
      {/if}
    </div>
  {/if}

  <!-- ── WRITING ── -->
  <div class="subhead sales"><span>Personas</span></div>

  {#if personaList.length === 0}
    <div class="empty-state"><div class="empty-state-text">No personas configured yet.</div></div>
  {:else}
    {#each personaList as [key, persona]}
      {@const human = isHuman(persona.label)}
      {@const isDefault = data?.active === key}
      <div class="pcard" class:open={openKey === key}>
        <!-- Toggle is a real <button> with only non-interactive content; actions
             are SIBLINGS. Nesting them made the row's accessible name swallow its
             children and forced stopPropagation. Chevron beside the title (§6). -->
        <div class="pcard-h">
          <!-- Won't collapse while editing — same guard as Today's draft head.
               Otherwise a stray row click discards what you were typing. -->
          <button class="pcard-toggle" aria-expanded={openKey === key}
            onclick={() => { if (editingKey === key) return; toggleOpen(key); }}>
            <span class="mark">
              {#if human}
                <span class="mono" aria-hidden="true">{initials(persona.label, key)}</span>
              {:else}
                <span class="spark">✦</span>
              {/if}
            </span>
            <span class="nm">{displayName(persona.label, key)}</span>
            <!-- Independent, not either/or: a persona can be BOTH the human voice
                 and the current default, and suppressing "Human" in that case
                 loses the distinction exactly when it matters most. -->
            {#if human}
              <span class="status-pill status-pill-grey">Human</span>
            {/if}
            {#if isDefault}
              <span class="status-pill status-pill-blue">Default</span>
            {/if}
            <span class="chev" aria-hidden="true">&rsaquo;</span>
          </button>
          <!-- No .sp spacer: .pcard-toggle is flex:1 and does the pushing. A .sp
               (also flex:1) would split the space and leave half the row dead. -->
          {#if !isDefault}
            <button class="today-mini-btn" onclick={() => handleSetActive(key)}>Make default</button>
          {/if}
          <button class="today-mini-btn" onclick={() => startEdit(key)}>Edit</button>
        </div>

        {#if openKey === key}
          <div class="pcard-b">
            {#if editingKey === key}
              <div class="ef" style="margin-bottom:10px;">
                <label for="pe-name-{key}">Name</label>
                <input id="pe-name-{key}" type="text" bind:value={editName} />
              </div>
              <!-- rows sized to real content: a ~1,500-char background through a
                   6-line porthole meant a permanent scrollbar + corner resizer. -->
              <div class="ef" style="margin-bottom:10px;">
                <label for="pe-bg-{key}">Background</label>
                <textarea id="pe-bg-{key}" rows="14" bind:value={editBg}></textarea>
              </div>
              <div class="ef" style="margin-bottom:10px;">
                <label for="pe-style-{key}">Writing style</label>
                <textarea id="pe-style-{key}" rows="5" bind:value={editStyle}></textarea>
              </div>
              <div class="row savebar" style="gap:6px;">
                <button class="btn-primary" onclick={handleSave} disabled={saving === key}>
                  {saving === key ? 'Saving...' : 'Save'}
                </button>
                <button class="today-mini-btn" onclick={cancelEdit}>Cancel</button>
              </div>
            {:else}
              <div class="fb">
                <div class="fk">
                  <span>Background</span>
                  <span class="sp"></span>
                  <span class="fkmeta">{(persona.background || '').length.toLocaleString()} characters</span>
                </div>
                <!-- Always full. The spec's 3-line clamp assumed the old build,
                     where every persona was expanded at once; this is an accordion
                     now, so you only meet the text in a card you opened. -->
                <div class="ft">{persona.background}</div>
              </div>
              <div class="fb">
                <div class="fk"><span>Style</span></div>
                <div class="ft">{persona.style}</div>
              </div>
            {/if}
          </div>
        {/if}
      </div>
    {/each}
  {/if}

  <!-- ── SPEAKING ── -->
  <!-- "Live voice", not "Speaking": it names the same thing the sidebar
       calls Live Calls, which is where the setting actually lives. -->
  <div class="subhead sales"><span>Live voice</span></div>
  <div class="card">
    <div class="frow">
      <div>
        <div class="lab">Voice on calls</div>
        <div class="hint">
          {$agentNameStore ? $agentNameStore.split(' ')[0] : 'George'} sounds different on a call than he
          reads on the page. That setting lives with Live Calls, not here.
        </div>
      </div>
      <div class="ctl">
        <button class="navlink" onclick={() => currentView.set('george')}>Live Calls →</button>
      </div>
    </div>
  </div>
</div>

<!-- ════════ KNOWLEDGE ════════ -->
<div class="pane" class:on={activeTab === 'knowledge'}>
  <KnowledgeBase onCount={(n) => knowledgeCount = n} />
</div>

<!-- ════════ COLLATERAL ════════ -->
<div class="pane" class:on={activeTab === 'collateral'}>
  <OutreachDocs onCount={(n) => collateralCount = n} />
</div>
{/if}

{#if toast}
  <div class="toast" class:toast-success={!toastError} class:toast-error={toastError}>{toast}</div>
{/if}

<style>
  .fund-doc-note { color: var(--text-muted); font-size: 13px; line-height: 1.6; }
  .fund-doc-meta { color: var(--text-dim); font-size: 11px; }
  .fund-upload { display: inline-flex; align-items: center; cursor: pointer; margin: 12px 0; }
  .fund-upload input { position: absolute; width: 1px; height: 1px; opacity: 0; }
  /* Buttons are the app's CANONICAL classes (`.btn-primary` / `.today-mini-btn`
     in app.css) — the same vocabulary Today and ClientView use, so radius (5px),
     padding, hover and disabled states are shared rather than re-guessed here.
     This block is only Today's own height unifier (its `.draft-actions` rule):
     base `.btn-primary` is 12.5px/6px vs mini 12px/5px, so without it a row
     holding both sits at two heights. */
  /* FILLED secondary. DESIGN-SYSTEM §2's middle tier — Send (solid coral) ›
     Edit/Cancel (filled neutral) › ✦ tools & Dismiss (transparent). The base
     `.today-mini-btn` in app.css is transparent; Today adds this fill in a
     scoped rule of its own, so reading only the base rule makes the tier look
     transparent. None of the buttons on this page are defer/drop, so they all
     fill. Hover is left to the base rule (brighter text + --border-strong). */
  .today-mini-btn { background: var(--bg-hover); }
  /* Panes stay mounted so tab counts and scroll position survive a switch. */
  .pane { display: none; }
  .pane.on { display: block; }

  /* Small-caps section label. DESIGN-SYSTEM.md typography note: one style for
     every label of this kind — .subhead, .pf .k, .ef label and .fb .fk below
     all share these four properties deliberately. */
  .sp { flex: 1; }
  .subhead { display: flex; align-items: center; gap: 8px; font-size: 11px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-dim); margin-bottom: 10px; }
  .subhead.sales { gap: 9px; min-height: 24px; margin: 26px 0 8px; font-size: 10px; font-family: var(--font-mono); letter-spacing: 0.11em; font-weight: normal; }
  .subhead.sales.first { margin-top: 4px; }
  /* Same prose treatment as Settings — see the note there. */
  .frow .hint {
    font-size: 11.5px;
    color: var(--text-dim);
    line-height: 1.5;
    max-width: 560px;
  }
  /* Red text, not a red box: a note about one card, not a page-level alert.
     The sentence carries the meaning, so §1's "never colour alone" holds. */
  .loadfail { font-size: 12.5px; color: var(--red); }

  /* profile read grid */
  .pgrid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 22px; }
  .pf.wide { grid-column: 1 / -1; }
  .pf .k {
    font-size: 10px;
    font-family: var(--font-mono);
    letter-spacing: 0.11em;
    text-transform: uppercase;
    color: var(--text-dim);
  }
  .pf .v { font-size: 13px; margin-top: 3px; }

  /* profile edit grid */
  .egrid { display: grid; grid-template-columns: 1fr 1fr; gap: 11px 18px; }
  .ef.wide { grid-column: 1 / -1; }
  .ef label {
    display: block;
    margin-bottom: 4px;
    font-size: 10px;
    font-family: var(--font-mono);
    letter-spacing: 0.11em;
    text-transform: uppercase;
    color: var(--text-dim);
  }
  /* The native corner resizer is drawn by the browser as a square grip and
     ignores `border-radius`, so it always crosses the rounded corner — Firefox
     additionally outlines it, which is the "border glitch". app.css sets
     `resize: vertical` globally; these fields are now sized to their content
     (rows=14 holds George's full ~1,500 chars with no overflow), so the handle
     bought nothing and cost a permanent artifact. Scrolling still works.
     Scoped to this page — Today's / ClientView's composers keep
     drag-resize, which may genuinely be wanted there. */
  .ef textarea { resize: none; }

  .editbar {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 13px;
    padding-top: 11px;
    border-top: 1px solid var(--border);
  }
  .editmeta { font-size: 11px; color: var(--text-dim); font-family: var(--font-mono); }


  /* field row — matches the Settings card row so both pages read alike */
  .frow { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; }
  .frow .lab { font-size: 13px; font-weight: 500; }
  .frow .hint { margin-top: 2px; }
  .frow .ctl { flex-shrink: 0; display: flex; align-items: center; gap: 8px; }

  /* persona cards */
  .pcard {
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--bg-surface);
    margin-bottom: 9px;
    box-shadow: var(--shadow-sm);
  }
  /* Whole row toggles. The toggle button is `flex: 1` so its real box spans
     everything left of the actions — no nesting, unlike Today's role="button"
     header (its actions sit in a separate row).
     ⚠️ Do NOT use an absolutely-positioned ::after overlay instead: elementFromPoint
     reports the <button>, but a click on the out-of-flow pseudo-element never fires
     its handler — the row looks clickable and silently isn't. */
  .pcard-h { display: flex; align-items: center; gap: 9px; padding: 12px 17px; }
  .pcard-toggle { flex: 1; }
  /* Real button, styled back to plain text so the row reads unchanged. */
  .pcard-toggle {
    display: flex;
    align-items: center;
    gap: 9px;
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;
    min-width: 0;
  }
  .pcard-toggle:hover .nm { color: var(--text); }
  /* Fixed-width slot so every persona name starts at the same x, whatever its
     mark. Widened 11px → 18px to hold the monogram; the ✦ centres in the same
     slot, so the two rows stay on one axis. */
  .pcard-h .mark {
    width: 18px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
  }
  /* Initials monogram for human personas — the counterpart to George's ✦.
     Quiet on purpose: it identifies, it doesn't compete with the name. */
  .pcard-h .mono {
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--border);
    border-radius: 50%;
    font-family: var(--font-mono);
    font-size: 8.5px;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: var(--text-dim);
  }
  .pcard-h .nm { font-size: 13px; font-weight: 600; }
  /* Rotation matches Today's `.draft-chev`: collapsed points DOWN ("expand ↓"),
     open points UP. DECISIONS 2026-07-16 — `→` means navigate, ↓/↑ mean
     expand/collapse, one direction for the action everywhere. */
  .pcard-h .chev {
    flex-shrink: 0;
    color: var(--text-dim);
    font-size: 15px;
    line-height: 1;
    transition: transform 0.12s;
    transform: rotate(90deg);
  }
  .pcard.open .chev { transform: rotate(270deg); }
  .pcard-b { padding: 0 17px 13px; }
  /* `.spark` is global (app.css). */

  .fb { border-top: 1px solid var(--border); padding-top: 10px; margin-top: 10px; }
  .fb .fk {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 5px;
    font-size: 10px;
    font-family: var(--font-mono);
    letter-spacing: 0.11em;
    text-transform: uppercase;
    color: var(--text-dim);
  }
  .fb .fkmeta { text-transform: none; letter-spacing: 0; }
  .fb .ft { font-size: 12.5px; color: var(--text-muted); line-height: 1.65; white-space: pre-wrap; }
  /* `.clamp` / `.more` removed with the "Show full" toggle — the background
     always renders in full now. */

  /* Secondary button — the filled neutral pill tier (DESIGN-SYSTEM.md §2). */
  /* Coral text-link + → : navigates to another view (§2 / §3). */
  /* Forward-verb text link — matches Today's `.ctask-btn` (12.5px / 600). */
</style>
